import { createFileRoute } from '@tanstack/react-router'
import { type } from 'arktype'
import { type ReactElement } from 'react'

import { assertDefinedAndNotNull } from '@orus.eu/error'
import { isSuccess } from '@orus.eu/result'

import { sessionManager } from '#/lib/session.ts'
import { AddPaymentMethodPageSkeleton } from '#/modules/payment/front-office/add-payment-method-page/add-payment-method-page-skeleton.tsx'
import { AddPaymentMethodPage } from '#/modules/payment/front-office/add-payment-method-page/add-payment-method-page.tsx'
import { FRONT_OFFICE_V2_QUERY_OPTIONS } from '#/shared/front-office-v2-query-options.ts'
import { requirePermissions } from '#/shared/routing/require-permissions.ts'

export const Route = createFileRoute('/(customer)/_shell/contracts-v2/$subscriptionId/payment-methods/new/')({
  validateSearch: type({ 'returnTo?': "'payment-methods'" }),
  beforeLoad: ({ context }) => requirePermissions(context, ['paymentAccount.update']),
  loader: async ({ context, params }) => {
    const sessionResult = await sessionManager.getSessionResult()
    const userId = isSuccess(sessionResult)
      ? assertDefinedAndNotNull(sessionResult.output.user, 'Payment methods require a logged-in user', {}).id
      : undefined

    return Promise.all([
      // `includeTerminatedBeforeOneMonth` must match the screen's own query: a different input is a different
      // cache key, and the loader would warm an entry the component never reads.
      context.queryClient.ensureQueryData(
        context.trpc.contracts.getMyContract.queryOptions(
          {
            subscriptionId: params.subscriptionId,
            includeTerminatedBeforeOneMonth: true,
          },
          FRONT_OFFICE_V2_QUERY_OPTIONS,
        ),
      ),
      context.queryClient.ensureQueryData(
        context.trpc.paymentMethod.getAllowedPaymentMethodsBySubscriptionId.queryOptions(
          {
            subscriptionId: params.subscriptionId,
          },
          FRONT_OFFICE_V2_QUERY_OPTIONS,
        ),
      ),
      context.queryClient.ensureQueryData(
        context.trpc.users.loadMyInformation.queryOptions(undefined, FRONT_OFFICE_V2_QUERY_OPTIONS),
      ),
      context.queryClient.ensureQueryData(
        context.trpc.contracts.getLastCustomerAddress.queryOptions(undefined, FRONT_OFFICE_V2_QUERY_OPTIONS),
      ),
      ...(userId
        ? [
            context.queryClient.ensureQueryData(
              context.trpc.paymentMethod.getDebitablePaymentMethods.queryOptions(
                { userId },
                FRONT_OFFICE_V2_QUERY_OPTIONS,
              ),
            ),
          ]
        : []),
    ])
  },
  pendingComponent: AddPaymentMethodPageSkeleton,
  pendingMs: 150,
  component: AddPaymentMethodRoute,
})

function AddPaymentMethodRoute(): ReactElement {
  const { subscriptionId } = Route.useParams()
  const { returnTo } = Route.useSearch()
  return <AddPaymentMethodPage subscriptionId={subscriptionId} returnTo={returnTo} />
}
